import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getSalesAmountApi,
  getActiveUsersCountApi,
  purchaseSalesApi,
  getProfitLossBalanceApi,
  getLastSevenMonthsPurchaseAndSalesApi,
} from "../../apiservice/dashboard";
import {
  getAccountBalanceApi,
  getSundryCreditorBalanceApi,
  getSundryDebitorBalanceApi,
} from "../../apiservice/accounts";
import dayjs from "dayjs";
import { currentDateStartTime, currentDateEndTime } from "../../lib/functions";
import { selectAuthInfo } from "../../pages/Auth/authSelectors";

export const fetchDashboardStats = createAsyncThunk(
  "dashboard/fetchDashboardStats",
  async (filter = "today", { getState, rejectWithValue }) => {
    try {
      const authInfo = selectAuthInfo(getState());

      const today = dayjs();
      let fromDate, toDate, prevFrom, prevTo;

      if (filter === "month") {
        // current month
        const monthStart = today.startOf("month");
        const monthEnd = today.endOf("month");
        fromDate = currentDateStartTime(monthStart);
        toDate = currentDateEndTime(monthEnd);

        // previous month
        const prevMonthStart = today.subtract(1, "month").startOf("month");
        const prevMonthEnd = today.subtract(1, "month").endOf("month");
        prevFrom = currentDateStartTime(prevMonthStart);
        prevTo = currentDateEndTime(prevMonthEnd);
      } else {
        // today
        fromDate = currentDateStartTime(today);
        toDate = currentDateEndTime(today);

        // yesterday
        const prevDate = today.subtract(1, "day");
        prevFrom = currentDateStartTime(prevDate);
        prevTo = currentDateEndTime(prevDate);
      }

      // 🚀 Fetch both current & previous in parallel
      const [
        profitLoss,
        prevProfitLoss,
        sales,
        prevSales,
        activeUsers,
        cashBalance,
        bankBalance,
        creditors,
        debtors,
        salesAndPurchase,
      ] = await Promise.all([
        getProfitLossBalanceApi({ fromDate, toDate, page: "dashboard" }),
        getProfitLossBalanceApi({ fromDate: prevFrom, toDate: prevTo, page: "dashboard" }),
        getSalesAmountApi({ fromDate, toDate, page: "dashboard" }),
        getSalesAmountApi({ fromDate: prevFrom, toDate: prevTo, page: "dashboard" }),
        getActiveUsersCountApi({
          fromDate,
          toDate,
          previousFromDate: prevFrom,
          previousToDate: prevTo,
        }),
        getAccountBalanceApi({ accType: "'cash_in_hand'", type: "head_total", page: "dashboard" }),
        getAccountBalanceApi({ accType: "'bank_account'", type: "head_total", page: "dashboard" }),
        getSundryCreditorBalanceApi({
          type: "head_total",
          accType: "'creditor'",
          page: "dashboard"
        }),
        getSundryDebitorBalanceApi({
          type: "head_total",
          accType: "'debitor'",
          page: "dashboard"
        }),
        purchaseSalesApi({ fromDate, toDate }),
      ]);

      const recentTransactions = (salesAndPurchase.data || [])
        .slice(0, 10)
        .map((r, idx) => ({
          id: idx,
          date: r.month ? dayjs(r.month + "-01").toISOString() : null,
          description: "Sales vs Purchase",
          amount: Number(r.total_sales || 0) - Number(r.total_purchase || 0),
          type: "sales_purchase",
        }));

      // ✅ Compute metrics
      const salesVal = Number(sales.data?.[0]?.total_amount || 0);
      const prevSalesVal = Number(prevSales.data?.[0]?.total_amount || 0);

      const grossProfit = Number(profitLoss.data?.grossProfitAmount || 0);
      const prevGrossProfit = Number(
        prevProfitLoss.data?.grossProfitAmount || 0
      );

      const netProfit = Number(profitLoss.data?.netProfit || 0);
      const prevNetProfit = Number(prevProfitLoss.data?.netProfit || 0);

      const activeUsersVal = Number(
        activeUsers.data?.newlyAdded?.user_count || 0
      );
      const prevUsers = Number(
        activeUsers.data?.previousAdded?.user_count || 0
      );

      // 📊 Compute percentage change safely
      const percentChange = (curr, prev) =>
        prev === 0 ? 0 : ((curr - prev) / prev) * 100;

      return {
        sales: salesVal,
        salesDiff: salesVal - prevSalesVal,
        salesTrend: percentChange(salesVal, prevSalesVal),
        grossProfit,
        grossProfitDiff: grossProfit - prevGrossProfit,
        grossProfitTrend: percentChange(grossProfit, prevGrossProfit),
        netProfit,
        netProfitDiff: netProfit - prevNetProfit,
        netProfitTrend: percentChange(netProfit, prevNetProfit),
        activeUsers: activeUsersVal,
        activeUsersDiff: activeUsersVal - prevUsers,
        activeUsersTrend: percentChange(activeUsersVal, prevUsers),
        cashBalance: Number(cashBalance.data?.total_balance || 0),
        bankBalance: Number(bankBalance.data?.total_balance || 0),
        receivables: Number(debtors.data?.total_balance || 0),
        payables: Number(creditors.data?.total_balance || 0),
        currency: authInfo?.currency,
        recentTransactions,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchDashboardLastSevenMonthSalesAndPurchase = createAsyncThunk(
  "dashboard/fetchDashboardLastSevenMonthSalesAndPurchase",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getLastSevenMonthsPurchaseAndSalesApi({});
      // return res.data;
      const result = res.data.map((item) => ({
        month: dayjs(item.month + "-01").format("MMMM"),
        sales: item.total_sales,
        purchase: item.total_purchase,
      }));
      return result;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
